import { trackEvent } from './gtm';

/**
 * PayGo Payment Success
 * Call after successful PayGo credit purchase
 */
export const trackPayGoPayment = (transactionId, amount, currency = 'USD', userId, couponCode = null) => {
  trackEvent('paygo_payment_success', {
    eventCategory: 'Payment',
    eventAction: 'PayGo Purchase',
    eventLabel: 'Pay-as-You-Go Credit',
    transactionId,
    revenue: amount,
    currency,
    userId,
    planType: 'paygo',
    ...(couponCode && { couponCode })
  });
};

/**
 * Subscription Payment Success
 * Call after successful Premium or Solo subscription purchase
 */
export const trackSubscriptionPayment = (transactionId, planType, amount, currency = 'USD', userId, couponCode = null) => {
  trackEvent('subscription_payment_success', {
    eventCategory: 'Payment',
    eventAction: 'Subscription Purchase',
    eventLabel: planType === 'premium' ? 'Premium Plan' : 'Solo Plan',
    transactionId,
    revenue: amount,
    currency,
    userId,
    planType,
    ...(couponCode && { couponCode })
  });
};

/**
 * User Signup
 * Call after successful user registration
 */
export const trackUserSignup = (userId, signupMethod) => {
  trackEvent('user_signup', {
    eventCategory: 'User',
    eventAction: 'Sign Up',
    eventLabel: signupMethod, // 'google', 'facebook', 'apple', 'linkedin', 'email'
    userId
  });
};

/**
 * User Login
 * Call after successful user authentication
 */
export const trackUserLogin = (userId, loginMethod) => {
  trackEvent('user_login', {
    eventCategory: 'User',
    eventAction: 'Login',
    eventLabel: loginMethod,
    userId
  });
};

/**
 * Referral Code Used
 * Call when new user signs up via referral link
 */
export const trackReferralUsed = (referralCode, referrerId, newUserId) => {
  trackEvent('referral_used', {
    eventCategory: 'Referral',
    eventAction: 'Code Used',
    eventLabel: referralCode,
    referralCode,
    referrerId,
    userId: newUserId
  });
};

/**
 * Plan Selected
 * Call when user selects a plan (Premium, Solo, PayGo)
 */
export const trackPlanSelection = (planType, userId = null) => {
  trackEvent('plan_selected', {
    eventCategory: 'Plan',
    eventAction: 'Plan Selected',
    eventLabel: planType,
    planType,
    userId
  });
};

/**
 * Checkout Started
 * Call when payment page loads
 */
export const trackCheckoutStarted = (planType, userId = null) => {
  trackEvent('checkout_started', {
    eventCategory: 'Checkout',
    eventAction: 'Checkout Started',
    eventLabel: planType,
    planType,
    userId
  });
};

/**
 * Auto-Reload Triggered
 * Call when PayGo auto-reload activates
 */
export const trackAutoReload = (amount, currency = 'USD', userId) => {
  trackEvent('auto_reload_triggered', {
    eventCategory: 'PayGo',
    eventAction: 'Auto-Reload',
    eventValue: amount,
    currency,
    userId
  });
};

/**
 * Referral Link Shared
 * Call when user copies referral link or downloads QR code
 */
export const trackReferralShare = (method, userId) => {
  trackEvent('referral_shared', {
    eventCategory: 'Referral',
    eventAction: 'Link Shared',
    eventLabel: method, // 'copy', 'qr_download'
    userId
  });
};

/**
 * Subscription Cancelled
 * Call when user cancels their subscription
 */
export const trackSubscriptionCancelled = (planType, userId, reason = null) => {
  trackEvent('subscription_cancelled', {
    eventCategory: 'Subscription',
    eventAction: 'Cancelled',
    eventLabel: planType,
    planType,
    userId,
    cancellationReason: reason
  });
};

/**
 * eSIM Activated
 * Call when eSIM is successfully activated
 */
export const trackEsimActivated = (userId, planType, iccid = null) => {
  trackEvent('esim_activated', {
    eventCategory: 'eSIM',
    eventAction: 'Activated',
    eventLabel: planType,
    planType,
    userId,
    iccid
  });
};

// =============================================================================
// Product Hunt Events
// =============================================================================

/**
 * Product Hunt Landing
 * Called automatically when a user lands from Product Hunt
 */
export const trackProductHuntLanding = (attribution) => {
  trackEvent('product_hunt_landing', {
    eventCategory: 'Product Hunt',
    eventAction: 'Landing',
    eventLabel: attribution.landingPage,
    campaign: attribution.campaign,
    medium: attribution.medium,
    content: attribution.content,
    referrer: attribution.referrer,
    visitorId: attribution.visitorId
  });
};

/**
 * Product Hunt Conversion
 * Call when a Product Hunt visitor completes a conversion action
 */
export const trackProductHuntConversion = (conversionType, attribution, additionalData = {}) => {
  trackEvent('product_hunt_conversion', {
    eventCategory: 'Product Hunt',
    eventAction: 'Conversion',
    eventLabel: conversionType,
    conversionType,
    campaign: attribution.campaign,
    medium: attribution.medium,
    landingPage: attribution.landingPage,
    timeToConvert: Date.now() - attribution.timestamp,
    visitorId: attribution.visitorId,
    ...additionalData
  });
};

/**
 * Product Hunt Signup
 * Call when a Product Hunt visitor creates an account
 */
export const trackProductHuntSignup = (userId, attribution) => {
  trackProductHuntConversion('signup', attribution, { userId });
};

/**
 * Product Hunt Purchase
 * Call when a Product Hunt visitor completes a purchase
 */
export const trackProductHuntPurchase = (userId, planType, revenue, attribution) => {
  trackProductHuntConversion('purchase', attribution, {
    userId,
    planType,
    revenue,
    currency: 'USD'
  });
};
